import * as THREE from "three";
import { humanSprite } from "./CharacterSprites";
import { makeContactShadow } from "./ContactShadow";
import { billboardMaterial, faceCameraYaw, facingFlipSign, SPRITE_FOOT_Y } from "./WorldArt";

/**
 * Ambient pier angler: one person casting off a dock edge.
 * Marker scenery supplies facing (toward water); line + bobber drift gently.
 */
export class ParkFishing {
  readonly group = new THREE.Group();
  private readonly mesh: THREE.Mesh;
  private readonly shadow: THREE.Mesh;
  private readonly bobber: THREE.Mesh;
  private readonly line: THREE.Line;
  private readonly ready: ReturnType<typeof humanSprite>;
  private readonly cast: ReturnType<typeof humanSprite>;
  private readonly x: number;
  private readonly z: number;
  private readonly y: number;
  private readonly facing: number;
  private readonly lineReach: number;
  private phase: number;
  private readonly period: number;

  constructor(
    x: number,
    z: number,
    groundY: (x: number, z: number) => number,
    facing: number,
    variant = 0,
  ) {
    this.x = x;
    this.z = z;
    this.y = groundY(x, z);
    this.facing = facing;
    this.phase = (variant % 7) * 0.13;
    this.period = 2.4 + (variant % 5) * 0.35;
    this.lineReach = 2.15 + (variant % 3) * 0.2;

    this.ready = humanSprite("fishReady", variant);
    this.cast = humanSprite("fishCast", variant);

    this.shadow = makeContactShadow({
      radius: Math.min(0.5, this.ready.width * 0.36),
      squash: 1,
      opacity: 0.82,
    });
    this.shadow.position.set(x, this.y + 0.02, z);

    this.mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(this.ready.width, this.ready.height),
      billboardMaterial(this.ready.texture),
    );
    this.mesh.position.set(x, this.y + this.ready.height * SPRITE_FOOT_Y, z);
    this.mesh.renderOrder = 3;

    const lineMat = new THREE.LineBasicMaterial({
      color: 0x90a4ae,
      transparent: true,
      opacity: 0.55,
      depthWrite: false,
    });
    this.line = new THREE.Line(new THREE.BufferGeometry(), lineMat);
    this.line.renderOrder = 2;

    this.bobber = new THREE.Mesh(
      new THREE.SphereGeometry(0.07, 6, 5),
      new THREE.MeshLambertMaterial({ color: 0xe53935 }),
    );
    this.bobber.renderOrder = 2;

    this.group.add(this.shadow, this.mesh, this.line, this.bobber);
    this.layoutLine(0);
  }

  update(dtSec: number, timeMs: number, camera: THREE.Camera): void {
    this.phase = (this.phase + dtSec / this.period) % 1;
    const casting = this.phase > 0.72 && this.phase < 0.88;
    setHumanMap(this.mesh, casting ? this.cast : this.ready);
    const bob = 1 + Math.sin(timeMs * 0.006 + this.x) * 0.01;
    this.mesh.position.y = this.y + this.ready.height * SPRITE_FOOT_Y * bob;
    faceCameraYaw(this.mesh, camera);
    const flip = facingFlipSign(this.x, this.z, this.facing, camera);
    this.mesh.scale.set(flip, 1, 1);
    this.layoutLine(timeMs);
  }

  destroy(): void {
    this.group.removeFromParent();
    this.mesh.geometry.dispose();
    this.line.geometry.dispose();
    this.bobber.geometry.dispose();
  }

  private layoutLine(timeMs: number): void {
    const handLift = 1.05;
    const hx = this.x + Math.cos(this.facing) * 0.35;
    const hz = this.z + Math.sin(this.facing) * 0.35;
    const hy = this.y + handLift;
    const tipX = this.x + Math.cos(this.facing) * this.lineReach;
    const tipZ = this.z + Math.sin(this.facing) * this.lineReach;
    // Bobber rides the water surface, not the raised pier deck.
    const tipY = -0.14 + Math.sin(timeMs * 0.003 + this.z) * 0.05;
    const midX = (hx + tipX) * 0.5;
    const midZ = (hz + tipZ) * 0.5;
    const midY = (hy + tipY) * 0.5 - 0.25;
    this.line.geometry.setFromPoints([
      new THREE.Vector3(hx, hy, hz),
      new THREE.Vector3(midX, midY, midZ),
      new THREE.Vector3(tipX, tipY, tipZ),
    ]);
    this.bobber.position.set(tipX, tipY, tipZ);
  }
}

function setHumanMap(
  mesh: THREE.Mesh,
  sheet: ReturnType<typeof humanSprite>,
): void {
  const mat = mesh.material;
  if (mat instanceof THREE.MeshBasicMaterial && mat.map !== sheet.texture) {
    mat.map = sheet.texture;
    mat.needsUpdate = true;
  }
}
